import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { GUID, MembershipType, PersonOrg, PersonOrgLink, PersonOrgType } from '@ndvr/customer-api';

import { themeDefault } from 'styles';
import { getPersonName, useRequiredParams } from 'utils';
import { usePersonOrgProfilesForHousehold } from 'views/person/person-queries';
import {
    Button,
    Card,
    FormPercentInput,
    getFieldName,
    HorizontalLayout,
    Ribbon,
    Select,
    Text,
    Title,
    VerticalLayout,
} from 'widgets';
import { IconClose } from 'widgets/icon';

export const MemberSelector = ({
    prefix,
    title,
    membershipType,
    showPercentageField,
}: {
    prefix: string;
    title: string;
    membershipType: MembershipType;
    showPercentageField?: boolean;
}) => {
    const fieldName = getFieldName(`members.${membershipType}`, prefix);
    const errorName = `${fieldName}-error`;
    const {
        setError,
        clearErrors,
        formState: { errors },
    } = useFormContext();
    const values: PersonOrgLink[] = useWatch({ name: fieldName });
    const { fields, append, remove } = useFieldArray({
        name: fieldName,
    });

    const [personToAdd, setPersonToAdd] = useState<GUID<PersonOrg> | undefined>(undefined);
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: personOrgs } = usePersonOrgProfilesForHousehold(globalAccountGUID);

    const availablePersons = personOrgs?.filter(
        (personOrgProfile) =>
            personOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON &&
            !fields.find(
                ({ id, ...fieldData }) =>
                    (fieldData as PersonOrgLink).memberPersonOrgGUID === personOrgProfile.personOrgGUID,
            ),
    );

    useEffect(() => {
        if (showPercentageField && values?.length > 0) {
            const percent = values.reduce((result, link) => result + Number(link.membershipPercent), 0);

            if (percent !== 100) {
                setError(errorName, {
                    type: 'custom',
                    message: 'Percentages must add up to 100%',
                });
            } else {
                clearErrors(errorName);
            }
        }
    }, [clearErrors, errorName, values, setError, showPercentageField]);

    const error = get(errors, errorName);

    return (
        <>
            <Card>
                <VerticalLayout $gap={24}>
                    <Title content>{title}</Title>
                    {error && <Ribbon intent="warning">Percentages must add up to 100%</Ribbon>}
                    <VerticalLayout>
                        {fields.length === 0 && <Text $italic>No person added</Text>}
                        <Grid>
                            {fields.map(({ id, ...fieldData }, idx) => {
                                const person = personOrgs?.find(
                                    (personOrgProfile) =>
                                        personOrgProfile.personOrgGUID ===
                                        (fieldData as PersonOrgLink).memberPersonOrgGUID,
                                );
                                return (
                                    <React.Fragment key={id}>
                                        <HorizontalLayout $gap={2}>
                                            <Text>{getPersonName(person?.personOrg)}</Text>
                                            <Text $themeColor="subtle" $italic>
                                                [{person?.personOrg.primaryEmail?.plainText}]
                                            </Text>
                                        </HorizontalLayout>
                                        {showPercentageField ? (
                                            <HorizontalLayout $rowCenter>
                                                <FormPercentInput
                                                    name={`${fieldName}.${idx}.membershipPercent`}
                                                    hideLabel
                                                />
                                                <Text>%</Text>
                                            </HorizontalLayout>
                                        ) : (
                                            <div></div>
                                        )}
                                        <RemoveIcon
                                            size="xSmall"
                                            onClick={() => remove(idx)}
                                            color={themeDefault.text.negative}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </Grid>
                    </VerticalLayout>
                    <HorizontalLayout $rowCenter $gap={16}>
                        <Select value={personToAdd} onChange={(ev) => setPersonToAdd(ev.target.value)}>
                            <option value={undefined}></option>
                            {availablePersons?.map(({ personOrg }) => (
                                <option key={personOrg.personOrgGUID} value={personOrg.personOrgGUID}>
                                    {getPersonName(personOrg)}
                                </option>
                            ))}
                        </Select>
                        <Button
                            size="small"
                            onClick={() => {
                                const selectedPersonOrg = availablePersons?.find(
                                    (personOrg) => personOrg.personOrgGUID === personToAdd,
                                );
                                if (!selectedPersonOrg) {
                                    return;
                                }

                                append({
                                    parentPersonOrgGUID: '',
                                    memberPersonOrgGUID: personToAdd,
                                    membershipType: membershipType,
                                    ...(showPercentageField ? { membershipPercent: fields.length > 0 ? 0 : 100 } : {}),
                                });

                                setPersonToAdd(undefined);
                            }}
                        >
                            Add
                        </Button>
                    </HorizontalLayout>
                </VerticalLayout>
            </Card>
        </>
    );
};

const RemoveIcon = styled(IconClose)`
    cursor: pointer;
`;

const Grid = styled.div`
    display: grid;
    align-items: center;
    column-gap: 24px;
    grid-template-columns: 1fr 66px 40px;
`;
